import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Loading } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";

const SCHEMES = ["Residential development", "Planning promotion", "Refurbishment", "Commercial", "Build to rent"];

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-5 py-10 md:py-16">
        <Logo className="h-9 mb-10" />
        <div className="eyebrow mb-2">EPIL JV Hub</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{title}</h1>
        <p className="mt-3 text-muted-foreground">{subtitle}</p>
        <div className="mt-8 rounded-lg border bg-card p-5 md:p-7">{children}</div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

export function Apply() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ company_name: "", company_number: "", contact_name: "", email: "", phone: "", scheme_types: [], track_record: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/public/apply", f); setSession(data.token, data.user); nav(`/onboarding/partners/${data.partner_id}`); }
    catch (er) { setErr(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Become a JV partner" subtitle="Tell us about your company. It takes two minutes – then you'll get a simple checklist to complete at your own pace.">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="apply-form">
        <F label="Company name"><Input required value={f.company_name} onChange={set("company_name")} data-testid="apply-company-name" /></F>
        <F label="Company number"><Input value={f.company_number} onChange={set("company_number")} placeholder="8 characters" data-testid="apply-company-number" /></F>
        <F label="Your full name"><Input required value={f.contact_name} onChange={set("contact_name")} data-testid="apply-contact-name" /></F>
        <F label="Phone"><Input value={f.phone} onChange={set("phone")} data-testid="apply-phone" /></F>
        <F label="Email"><Input required type="email" value={f.email} onChange={set("email")} data-testid="apply-email" /></F>
        <F label="Choose a password (8+ characters)"><Input required type="password" minLength={8} value={f.password} onChange={set("password")} data-testid="apply-password" /></F>
        <div className="sm:col-span-2 space-y-2"><Label className="text-xs">Scheme types</Label>
          <div className="flex flex-wrap gap-3">{SCHEMES.map((s) => <label key={s} className="flex items-center gap-2 text-sm"><Checkbox checked={f.scheme_types.includes(s)}
            onCheckedChange={(v) => setF({ ...f, scheme_types: v ? [...f.scheme_types, s] : f.scheme_types.filter((x) => x !== s) })} data-testid={`apply-scheme-${s.split(" ")[0].toLowerCase()}`} />{s}</label>)}</div></div>
        <div className="sm:col-span-2"><F label="Track record summary"><Textarea value={f.track_record} onChange={set("track_record")} placeholder="e.g. 12 schemes delivered since 2015, mainly 20–60 unit residential in Hampshire" data-testid="apply-track-record" /></F></div>
        {err && <div className="sm:col-span-2 text-sm font-medium text-primary" data-testid="apply-error">{err}</div>}
        <div className="sm:col-span-2 flex items-center justify-between gap-3">
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Already have an account? Sign in</Link>
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="apply-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Submit application</Button>
        </div>
      </form>
    </Shell>
  );
}

export function Join() {
  const { token } = useParams();
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/public/invite/${token}`).then((r) => setInfo(r.data)).catch((e) => setInfo({ error: errMsg(e) })); }, [token]);
  if (!info) return <Loading />;
  const go = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.post(`/public/invite/${token}`, { password: pw }); setSession(data.token, data.user); nav(`/onboarding/partners/${data.user.partner_id}`); }
    catch (er) { setErr(errMsg(er)); }
  };
  if (info.error) return <Shell title="Invite link" subtitle={info.error}><Link to="/login" className="font-semibold text-primary">Go to sign in</Link></Shell>;
  return (
    <Shell title={`Welcome, ${info.name}`} subtitle={`You've been invited to onboard ${info.company_name} as an EPIL JV partner. Set a password to begin.`}>
      <form onSubmit={go} className="space-y-4" data-testid="join-form">
        <F label="Email"><Input value={info.email} disabled /></F>
        <F label="Password (8+ characters)"><Input type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="join-password" /></F>
        {err && <div className="text-sm font-medium text-primary" data-testid="join-error">{err}</div>}
        <Button type="submit" className="rounded-full" data-testid="join-submit">Start onboarding</Button>
      </form>
    </Shell>
  );
}
